import React from "react";
import "@/App.css";
import "./i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { CanadianPayrollProvider } from "./context/CanadianPayrollContext";
import { PushProvider } from "./context/PushContext";
import { LocationProvider } from "./context/LocationContext";
import ErrorBoundary from "./components/ErrorBoundary";
import TrialGuard from "./components/TrialGuard";

// Pages
import Landing from "./pages/Landing";
import LifetimeRedeem from "./pages/LifetimeRedeem";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Staff from "./pages/Staff";
import TimeClock from "./pages/TimeClock";
import Schedule from "./pages/Schedule";
import Tasks from "./pages/Tasks";
import Announcements from "./pages/Announcements";
import Documents from "./pages/Documents";
import Reports from "./pages/Reports";
import Timesheets from "./pages/Timesheets";
import ExecutiveInsights from "./pages/ExecutiveInsights";
import Settings from "./pages/Settings";
import SuperAdmin from "./pages/SuperAdmin";
import Availability from "./pages/Availability";
import Notifications from "./pages/Notifications";
import ShiftSwaps from "./pages/ShiftSwaps";
import Absences from "./pages/Absences";
import Billing from "./pages/Billing";
import Support from "./pages/Support";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Kiosk from "./pages/Kiosk";
import AuthCallback from "./pages/AuthCallback";
import VerifyEmail from "./pages/VerifyEmail";
import AccountSecurity from "./pages/AccountSecurity";
import { SubscriptionSuccess, SubscriptionCancel } from "./pages/Subscription";
import FeatureRequest from "./pages/FeatureRequest";
import AuditLog from "./pages/AuditLog";
import AdminHolidays from "./pages/AdminHolidays";
import Leave from "./pages/Leave";
import LeaveCalendar from "./pages/LeaveCalendar";
import LeaveTypes from "./pages/LeaveTypes";
import LeaveImport from "./pages/LeaveImport";
import ShiftReplacements from "./pages/ShiftReplacements";
import Payslips from "./pages/Payslips";
import Brand from "./pages/Brand";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Pricing from "./pages/Pricing";
import Features from "./pages/Features";
import WorkforceManagement from "./pages/WorkforceManagement";
import Contact from "./pages/Contact";
import HelpArticle from "./pages/HelpArticle";
import HelpCenter from "./pages/HelpCenter";
import IndustryPage from "./pages/IndustryPage";
import EmployeeScheduling from "./pages/features/EmployeeScheduling";
import TimeTracking from "./pages/features/TimeTracking";
import TimesheetSoftware from "./pages/features/TimesheetSoftware";
import LaborCost from "./pages/features/LaborCost";
import TaskManagement from "./pages/features/TaskManagement";

// Employee Portal pages
import EmployeeDashboard from "./pages/employee/EmployeeDashboard";
import EmployeeSchedule from "./pages/employee/EmployeeSchedule";
import EmployeeTimesheets from "./pages/employee/EmployeeTimesheets";
import EmployeeAnnouncements from "./pages/employee/EmployeeAnnouncements";
import EmployeeDocuments from "./pages/employee/EmployeeDocuments";
import EmployeeNotifications from "./pages/employee/EmployeeNotifications";
import EmployeeProfile from "./pages/employee/EmployeeProfile";

// Protected Route component
const ProtectedRoute = ({
  children,
  allowedRoles = [],
  staffOnly = false,
  skipStaffCheck = false,
  skipOnboardingCheck = false,
  skipPasswordCheck = false,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force password change if must_change_password is true
  if (!skipPasswordCheck &&
      user.must_change_password &&
      location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Redirect to onboarding if user doesn't have a restaurant_group_id
  // (skip for super_admin and staff — staff get their group_id from their employer)
  if (!skipOnboardingCheck &&
      user.role !== 'super_admin' &&
      user.role !== 'staff' &&
      !user.restaurant_group_id &&
      location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Staff-only route: non-staff users redirected to /dashboard
  if (staffOnly) {
    if (user.role !== 'staff') {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  }

  // Non-employee routes: staff users always redirected to /employee
  // (unless skipStaffCheck is set, e.g. /change-password)
  if (!skipStaffCheck && user.role === 'staff') {
    return <Navigate to="/employee" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (user) {
    // Staff always go to employee portal
    if (user.role === 'staff') {
      return <Navigate to="/employee" replace />;
    }
    // If user doesn't have restaurant_group_id, go to onboarding instead
    if (user.role !== 'super_admin' && !user.restaurant_group_id) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      {/* Hidden marketplace redemption — never linked from the app/nav */}
      <Route path="/lifetime" element={<LifetimeRedeem />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/brand" element={<Brand />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      {/* Marketing pages — indexed in sitemap */}
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/workforce-management" element={<WorkforceManagement />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/employee-scheduling" element={<EmployeeScheduling />} />
      <Route path="/time-tracking" element={<TimeTracking />} />
      <Route path="/timesheet-software" element={<TimesheetSoftware />} />
      <Route path="/labor-cost-management" element={<LaborCost />} />
      <Route path="/task-management" element={<TaskManagement />} />
      <Route path="/restaurants" element={<IndustryPage />} />
      <Route path="/retail" element={<IndustryPage />} />
      <Route path="/clinics" element={<IndustryPage />} />
      <Route path="/salons" element={<IndustryPage />} />
      <Route path="/construction" element={<IndustryPage />} />
      <Route path="/agencies" element={<IndustryPage />} />
      <Route path="/kiosk" element={<Kiosk />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/verify-email" element={<VerifyEmail />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      {/* Change Password (for first login) */}
      <Route
        path="/change-password"
        element={
          <ProtectedRoute skipOnboardingCheck={true} skipPasswordCheck={true} skipStaffCheck={true}>
            <ChangePassword />
          </ProtectedRoute>
        }
      />

      {/* Onboarding */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute skipOnboardingCheck={true}>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - All authenticated users */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <Dashboard />
            </TrialGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-clock"
        element={
          <ProtectedRoute>
            <TimeClock />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <Schedule />
            </TrialGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <Tasks />
            </TrialGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/announcements"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <Announcements />
            </TrialGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <Documents />
            </TrialGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/availability"
        element={
          <ProtectedRoute skipStaffCheck={true}>
            <Availability />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shift-swaps"
        element={
          <ProtectedRoute>
            <ShiftSwaps />
          </ProtectedRoute>
        }
      />
      <Route
        path="/absences"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <Absences />
            </TrialGuard>
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Managers and Owners */}
      <Route
        path="/staff"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <Staff />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timesheets"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <Timesheets />
            </TrialGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/executive-insights"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <TrialGuard>
              <ExecutiveInsights />
            </TrialGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <Reports />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Owners only */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute allowedRoles={['owner', 'super_admin']}>
            <Billing />
          </ProtectedRoute>
        }
      />

      {/* Account Security — Owners, Managers, and Super Admin */}
      <Route
        path="/account-security"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <AccountSecurity />
          </ProtectedRoute>
        }
      />

      {/* Support - All authenticated users (including staff) */}
      <Route
        path="/support"
        element={
          <ProtectedRoute skipStaffCheck>
            <Support />
          </ProtectedRoute>
        }
      />

      {/* Feature Request - Owner only */}
      <Route
        path="/feature-request"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager']}>
            <FeatureRequest />
          </ProtectedRoute>
        }
      />

      {/* Eva Audit Log - Owner and Manager */}
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <TrialGuard>
              <AuditLog />
            </TrialGuard>
          </ProtectedRoute>
        }
      />

      {/* Holidays Admin - Owner and Manager */}
      <Route
        path="/admin/holidays"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <TrialGuard>
              <AdminHolidays />
            </TrialGuard>
          </ProtectedRoute>
        }
      />

      {/* Leave Management - all authenticated users */}
      <Route
        path="/leave"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <Leave />
            </TrialGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leave/calendar"
        element={
          <ProtectedRoute>
            <TrialGuard>
              <LeaveCalendar />
            </TrialGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leave/types"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <TrialGuard>
              <LeaveTypes />
            </TrialGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leave/import"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <TrialGuard>
              <LeaveImport />
            </TrialGuard>
          </ProtectedRoute>
        }
      />

      {/* Shift Replacements inbox — all authenticated users (incl. staff) */}
      <Route
        path="/shift-replacements"
        element={
          <ProtectedRoute skipStaffCheck={true}>
            <TrialGuard>
              <ShiftReplacements />
            </TrialGuard>
          </ProtectedRoute>
        }
      />

      {/* Payslips - All authenticated roles */}
      <Route
        path="/payslips"
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'staff']}>
            <Payslips />
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Super Admin only */}
      <Route
        path="/super-admin"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdmin />
          </ProtectedRoute>
        }
      />

      {/* Subscription Routes */}
      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/subscription/cancel" element={<SubscriptionCancel />} />

      {/* Employee Self-Service Portal — Staff only */}
      <Route
        path="/employee"
        element={
          <ProtectedRoute staffOnly skipPasswordCheck={false}>
            <EmployeeDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/schedule"
        element={
          <ProtectedRoute staffOnly>
            <EmployeeSchedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/timesheets"
        element={
          <ProtectedRoute staffOnly>
            <EmployeeTimesheets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/announcements"
        element={
          <ProtectedRoute staffOnly>
            <EmployeeAnnouncements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/documents"
        element={
          <ProtectedRoute staffOnly>
            <EmployeeDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/notifications"
        element={
          <ProtectedRoute staffOnly>
            <EmployeeNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/profile"
        element={
          <ProtectedRoute staffOnly>
            <EmployeeProfile />
          </ProtectedRoute>
        }
      />

      {/* Help Articles (public, no auth required) */}
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/help/:slug" element={<HelpArticle />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <CurrencyProvider>
            <LocationProvider>
              <PushProvider>
                <CanadianPayrollProvider>
                  <AppRoutes />
                  <Toaster position="top-right" richColors />
                </CanadianPayrollProvider>
              </PushProvider>
            </LocationProvider>
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
